import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Trophy, Crown, Medal, ArrowLeft, Users, TrendingUp, 
  Building, GraduationCap, MapPin, Star, Filter
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CodeArenaRankingsPage = () => {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    fetchRankings();
  }, [period]);

  const fetchRankings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/codearena/rankings?period=${period}&limit=100`);
      setRankings(res.data.rankings || []);
      setTotalParticipants(res.data.total_participants || 0);
    } catch (err) {
      console.error('Failed to fetch rankings:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) {
      return (
        <div className="w-10 h-10 bg-gradient-to-br from-[#915907] to-[#b87708] rounded-full flex items-center justify-center shadow-lg">
          <Crown className="text-white" size={20} />
        </div>
      );
    }
    if (rank === 2) {
      return (
        <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center shadow-md">
          <Medal className="text-white" size={18} />
        </div>
      );
    }
    if (rank === 3) {
      return (
        <div className="w-10 h-10 bg-gradient-to-br from-[#b24020] to-[#d35030] rounded-full flex items-center justify-center shadow-md">
          <Medal className="text-white" size={18} />
        </div>
      );
    }
    return (
      <div className="w-10 h-10 bg-[#f3f2ef] rounded-full flex items-center justify-center border border-gray-200">
        <span className="text-sm font-bold text-[#666666]">{rank}</span>
      </div>
    );
  };

  const getPercentileColor = (percentile) => {
    if (percentile >= 95) return 'text-[#915907]';
    if (percentile >= 80) return 'text-[#0a66c2]';
    if (percentile >= 50) return 'text-[#057642]';
    return 'text-[#666666]';
  };

  return (
    <div className="min-h-screen bg-[#f3f2ef]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-3">
            <Link 
              to="/codearena" 
              className="flex items-center gap-2 text-[#666666] hover:text-[#191919] transition-colors text-sm"
              data-testid="back-to-codearena"
            >
              <ArrowLeft size={16} />
              Back to EvalentX Arena
            </Link>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-[#191919] flex items-center gap-2">
                <Trophy className="text-[#915907]" size={28} />
                National Developer Rankings
              </h1>
              <p className="text-[#666666] text-sm mt-0.5">{totalParticipants.toLocaleString()} developers competing</p>
            </div>
            
            {/* Period Filter */}
            <div className="flex items-center gap-2">
              {['all', 'monthly', 'weekly'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-4 py-2 rounded-full font-medium transition-all text-sm ${
                    period === p 
                      ? 'bg-[#0a66c2] text-white' 
                      : 'bg-white text-[#666666] hover:bg-gray-50 border border-gray-200'
                  }`}
                  data-testid={`period-${p}`}
                >
                  {p === 'all' ? 'All Time' : p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Top 3 Podium */}
        {rankings.length >= 3 && (
          <div className="mb-8">
            <div className="grid md:grid-cols-3 gap-4 items-end">
              {/* 2nd Place */}
              <div className="order-1 md:order-1">
                <div className="bg-white border border-gray-200 rounded-xl p-5 text-center shadow-sm">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full mx-auto mb-3 flex items-center justify-center shadow-md">
                    <Medal className="text-white" size={28} />
                  </div>
                  <div className="text-lg font-semibold text-[#191919] mb-0.5">{rankings[1]?.full_name || 'Developer'}</div>
                  <div className="text-sm text-[#666666] mb-2">{rankings[1]?.college || 'Unknown'}</div>
                  <div className="text-2xl font-bold text-gray-500">{rankings[1]?.total_score || 0}</div>
                  <div className="text-xs text-[#666666]">points</div>
                </div>
              </div>

              {/* 1st Place */}
              <div className="order-0 md:order-2">
                <div className="bg-white border-2 border-[#915907]/30 rounded-xl p-6 text-center transform md:scale-105 shadow-md">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#915907] to-[#b87708] rounded-full mx-auto mb-3 flex items-center justify-center shadow-lg">
                    <Crown className="text-white" size={36} />
                  </div>
                  <div className="inline-flex items-center gap-1.5 bg-[#915907]/10 px-3 py-1 rounded-full mb-2">
                    <Star className="text-[#915907]" size={12} />
                    <span className="text-[#915907] text-xs font-bold">#1 NATIONAL</span>
                  </div>
                  <div className="text-xl font-bold text-[#191919] mb-0.5">{rankings[0]?.full_name || 'Developer'}</div>
                  <div className="text-sm text-[#666666] mb-2">{rankings[0]?.college || 'Unknown'}</div>
                  <div className="text-3xl font-bold text-[#915907]">{rankings[0]?.total_score || 0}</div>
                  <div className="text-xs text-[#666666]">points</div>
                </div>
              </div>

              {/* 3rd Place */}
              <div className="order-2 md:order-3">
                <div className="bg-white border border-gray-200 rounded-xl p-5 text-center shadow-sm">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#b24020] to-[#d35030] rounded-full mx-auto mb-3 flex items-center justify-center shadow-md">
                    <Medal className="text-white" size={28} />
                  </div>
                  <div className="text-lg font-semibold text-[#191919] mb-0.5">{rankings[2]?.full_name || 'Developer'}</div>
                  <div className="text-sm text-[#666666] mb-2">{rankings[2]?.college || 'Unknown'}</div>
                  <div className="text-2xl font-bold text-[#b24020]">{rankings[2]?.total_score || 0}</div>
                  <div className="text-xs text-[#666666]">points</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full Rankings Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-gray-100 bg-[#f3f2ef]">
            <h2 className="font-semibold text-[#191919]">All Rankings</h2>
          </div>

          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse"></div>
              ))}
            </div>
          ) : rankings.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {/* Header */}
              <div className="grid grid-cols-12 gap-4 px-5 py-3 text-sm font-medium text-[#666666] bg-[#f3f2ef]">
                <div className="col-span-1">Rank</div>
                <div className="col-span-4">Developer</div>
                <div className="col-span-3">College</div>
                <div className="col-span-2 text-center">Problems</div>
                <div className="col-span-2 text-right">Score</div>
              </div>

              {/* Rows */}
              {rankings.map((user, idx) => (
                <div 
                  key={idx}
                  className={`grid grid-cols-12 gap-4 px-5 py-3 items-center hover:bg-gray-50 transition-colors ${
                    idx < 3 ? 'bg-[#f3f2ef]/50' : ''
                  }`}
                  data-testid={`rank-row-${idx}`}
                >
                  <div className="col-span-1">
                    {getRankBadge(user.rank)}
                  </div>
                  
                  <div className="col-span-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-[#e8f4f8] rounded-full flex items-center justify-center border border-[#0a66c2]/20">
                        <span className="text-sm font-bold text-[#0a66c2]">
                          {user.full_name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-[#191919] text-sm">{user.full_name || 'Developer'}</div>
                        <div className="text-xs text-[#666666] flex items-center gap-1">
                          <GraduationCap size={10} />
                          {user.year_of_study || 'Student'} • {user.department || 'CS'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-span-3">
                    <div className="flex items-center gap-1.5 text-[#666666] text-sm">
                      <Building size={12} />
                      <span className="truncate">{user.college || 'Unknown'}</span>
                    </div>
                  </div>
                  
                  <div className="col-span-2 text-center">
                    <span className="text-[#666666] text-sm">{user.problems_solved || 0} solved</span>
                  </div>
                  
                  <div className="col-span-2 text-right">
                    <div className="inline-flex flex-col items-end">
                      <span className="text-lg font-bold text-[#0a66c2]">{user.total_score || 0}</span>
                      <span className={`text-xs font-medium ${getPercentileColor(user.percentile)}`}>
                        Top {(100 - (user.percentile || 0)).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Trophy className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="font-semibold text-[#191919] mb-2">No Rankings Yet</h3>
              <p className="text-[#666666] text-sm mb-4">Be the first to solve problems and climb the leaderboard!</p>
              <Link 
                to="/codearena/problems" 
                className="inline-block px-5 py-2 bg-[#0a66c2] text-white rounded-full font-medium hover:bg-[#004182] transition-colors text-sm"
              >
                Start Solving
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeArenaRankingsPage;
